import * as CookieConsent from 'vanilla-cookieconsent';
import i18n, { loadLanguageAsync } from '@monolith/legacy/services/i18n';
import { initConsent, onConsentChange } from './cookie-consent';
import { updateConsentMode, pushVendorConsent } from './consent-mode';

/**
 * Builds the vanilla-cookieconsent translations from the app's i18n keys
 * (`cookieConsent.*` in client/lang/*.json) so the banner copy goes through the
 * standard translation pipeline. Keys are passed as literals (not via a dynamic
 * lookup) so the i18n lint rule and the extract-fe key scanner can see them.
 *
 * Each preferences section carries a `cookieTable` (the cookie disclosure shown
 * under "Manage preferences"). Cookie names / providers / retention are literal
 * data; the column headers and per-row purposes are translated. Vendors being
 * withdrawn (Criteo, ID5) and undeclared trackers still under DPO triage (Adobe
 * Audience Manager, Nativo) are intentionally omitted from the disclosure.
 */
function buildTranslations(): CookieConsent.Translation {
  // Same columns for every category; built once with literal keys and reused.
  const headers = {
    name: i18n.global.t('cookieConsent.table.name.lbl') as string,
    provider: i18n.global.t('cookieConsent.table.provider.lbl') as string,
    purpose: i18n.global.t('cookieConsent.table.purpose.lbl') as string,
    retention: i18n.global.t('cookieConsent.table.retention.lbl') as string,
  };

  return {
    consentModal: {
      title: i18n.global.t('cookieConsent.banner.title') as string,
      description: i18n.global.t('cookieConsent.banner.description') as string,
      acceptAllBtn: i18n.global.t('cookieConsent.acceptAll.cta') as string,
      acceptNecessaryBtn: i18n.global.t('cookieConsent.rejectAll.cta') as string,
      showPreferencesBtn: i18n.global.t('cookieConsent.managePreferences.cta') as string,
    },
    preferencesModal: {
      title: i18n.global.t('cookieConsent.preferences.title') as string,
      acceptAllBtn: i18n.global.t('cookieConsent.acceptAll.cta') as string,
      acceptNecessaryBtn: i18n.global.t('cookieConsent.rejectAll.cta') as string,
      savePreferencesBtn: i18n.global.t('cookieConsent.savePreferences.cta') as string,
      closeIconLabel: i18n.global.t('cookieConsent.close.lbl') as string,
      sections: [
        {
          title: i18n.global.t('cookieConsent.necessary.title') as string,
          description: i18n.global.t('cookieConsent.necessary.description') as string,
          linkedCategory: 'necessary',
          cookieTable: {
            headers,
            body: [
              {
                name: 'ankorstore_session, aks-auth, browser_id',
                provider: 'Ankorstore',
                purpose: i18n.global.t('cookieConsent.cookie.session.purpose') as string,
                retention: 'Session / 1 year',
              },
              {
                name: 'cc_cookie',
                provider: 'Ankorstore',
                purpose: i18n.global.t('cookieConsent.cookie.consent.purpose') as string,
                retention: '6 months',
              },
              {
                name: '__stripe_mid, __stripe_sid',
                provider: 'Stripe',
                purpose: i18n.global.t('cookieConsent.cookie.stripe.purpose') as string,
                retention: '1 year / Session',
              },
            ],
          },
        },
        {
          title: i18n.global.t('cookieConsent.analytics.title') as string,
          description: i18n.global.t('cookieConsent.analytics.description') as string,
          linkedCategory: 'analytics',
          cookieTable: {
            headers,
            body: [
              {
                name: '_ga, _ga_*',
                provider: 'Google Analytics',
                purpose: i18n.global.t('cookieConsent.cookie.ga.purpose') as string,
                retention: '13 months',
              },
              {
                name: '_hjSession*, _hjSessionUser_*',
                provider: 'Hotjar',
                purpose: i18n.global.t('cookieConsent.cookie.hotjar.purpose') as string,
                retention: 'Session / 1 year',
              },
              {
                name: 'ajs_anonymous_id',
                provider: 'Segment',
                purpose: i18n.global.t('cookieConsent.cookie.segment.purpose') as string,
                retention: '1 year',
              },
            ],
          },
        },
        {
          title: i18n.global.t('cookieConsent.marketing.title') as string,
          description: i18n.global.t('cookieConsent.marketing.description') as string,
          linkedCategory: 'marketing',
          cookieTable: {
            headers,
            body: [
              {
                name: '_gcl_au, test_cookie',
                provider: 'Google Ads',
                purpose: i18n.global.t('cookieConsent.cookie.googleAds.purpose') as string,
                retention: '90 days / Session',
              },
              {
                name: '_fbp',
                provider: 'Meta',
                purpose: i18n.global.t('cookieConsent.cookie.meta.purpose') as string,
                retention: '90 days',
              },
              {
                name: '_uetsid, _uetvid, MUID',
                provider: 'Microsoft Bing',
                purpose: i18n.global.t('cookieConsent.cookie.bing.purpose') as string,
                retention: '1 day / 16 months',
              },
              {
                name: 'prism_*',
                provider: 'Braze',
                purpose: i18n.global.t('cookieConsent.cookie.braze.purpose') as string,
                retention: '1 year',
              },
            ],
          },
        },
      ],
    },
  };
}

export async function setupCookieConsent(): Promise<void> {
  // Ensure the active locale (set on <html lang>) is loaded before reading keys.
  // loadLanguageAsync is idempotent and mirrors the app's own locale setup.
  const language = document.documentElement.lang || 'en';
  await loadLanguageAsync(language, i18n);

  onConsentChange((categories) => {
    updateConsentMode(categories);
    // Reproduce the Didomi `didomiVendorsConsent` dataLayer signal the existing GTM
    // conversion triggers still gate on (see pushVendorConsent). Without it, every
    // Google Ads / GA4 / Meta / Snap / Bing / Criteo / Braze / Affilae conversion tag
    // stays blocked after the Didomi removal.
    pushVendorConsent(categories);
  });
  await initConsent({
    hostname: globalThis.location.hostname,
    language,
    translations: { [language]: buildTranslations() },
  });
  (globalThis as unknown as { CookieConsent: typeof CookieConsent }).CookieConsent = CookieConsent;
}
